<template>
  <div class="pb-3">
    <div class="text-center">
      <div class="file-drop-area">
        <div class="file-wrapper mb-3 position-relative">
          <div v-if="loading" class="file-overlay">
            <span class="upload-cog"><i class="fa fa-cog fa-spin"></i></span>
          </div>
          <input v-if="!loading" class="file-input" type="file" ref="file" @change="loadImage($event)" accept="image/*">
          <img class="crop-image" :src="imageUrl || '/no-image.png'" alt="">
        </div>
      </div>
      <div v-if="imageUrl" class="d-flex mb-3" style="gap: 8px;">
        <a :href="imageUrl" target="_blank" class="btn btn-info flex-grow-1">
          <i class="fa fa-eye mr-1"></i>{{ $t('action.preview') }}
        </a>
        <label class="btn btn-warning flex-grow-1 mb-0">
          <i class="fa fa-refresh mr-1"></i>{{ $t('tooltip.replaceImage') }}
          <input type="file" @change="loadImage($event)" accept="image/*" style="display:none">
        </label>
        <button type="button" @click="deleteImage()" class="btn btn-danger flex-grow-1">
          <i class="fa fa-trash mr-1"></i>{{ $t('delete') }}
        </button>
      </div>
    </div>
    <modal :name="`crop-modal-${_uid}`" :adaptive="true" height="auto" width="90%">
      <div class="d-flex justify-content-end p-2">
        <button type="button" class="btn btn-sm btn-light" @click="cancelCrop()" v-tooltip="$t('close')">
          <i class="fa fa-times"></i>
        </button>
      </div>
      <cropper
        v-if="image.src"
        ref="cropper"
        class="ntm-cropper"
        :stencil-component="circle ? 'circle-stencil' : 'rectangle-stencil'"
        :canvas="{
          maxWidth: maxWidth,
          maxHeight: maxHeight,
        }"
        image-restriction="none"
        :stencil-props="{
          aspectRatio: aspectRatio,
        }"
        :src="image.src"
        :transitions="false"
        @change="onCropChange"
      />
      <div class="crop-footer">
        <div v-if="cropResult.image" class="crop-preview-wrap" :class="{ 'crop-preview-circle': circle }">
          <Preview
            :width="70"
            :height="70"
            :image="cropResult.image"
            :coordinates="cropResult.coordinates"
          />
        </div>
        <div v-else style="width: 70px;"></div>
        <div class="crop-controls">
          <button type="button" class="btn btn-sm btn-light" @click="zoomIn()" v-tooltip="$t('tooltip.zoomIn')">
            <i class="fa fa-search-plus"></i>
          </button>
          <button type="button" class="btn btn-sm btn-light" @click="zoomOut()" v-tooltip="$t('tooltip.zoomOut')">
            <i class="fa fa-search-minus"></i>
          </button>
          <button type="button" class="btn btn-sm btn-light" @click="centerCrop()" v-tooltip="$t('tooltip.centerImage')">
            <i class="fa fa-crosshairs"></i>
          </button>
          <button type="button" class="btn btn-sm btn-light" @click="resetCropper()" v-tooltip="$t('tooltip.resetZoom')">
            <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="8" cy="8" r="5.5" stroke-width="2"/>
              <line x1="12.2" y1="12.2" x2="18" y2="18" stroke-width="2"/>
              <line x1="5.5" y1="5.5" x2="10.5" y2="10.5" stroke-width="1.8"/>
              <line x1="10.5" y1="5.5" x2="5.5" y2="10.5" stroke-width="1.8"/>
            </svg>
          </button>
          <button type="button" class="btn btn-sm btn-light" @click="rotateLeft()" v-tooltip="$t('tooltip.rotateLeft')">
            <i class="fa fa-rotate-left"></i>
          </button>
          <button type="button" class="btn btn-sm btn-light" @click="rotateRight()" v-tooltip="$t('tooltip.rotateRight')">
            <i class="fa fa-rotate-right"></i>
          </button>
          <label class="btn btn-sm btn-light mb-0" v-tooltip="$t('tooltip.replaceImage')">
            <i class="fa fa-refresh"></i>
            <input type="file" @change="loadImage($event)" accept="image/*" style="display:none">
          </label>
        </div>
        <button type="button" v-if="image.src" class="btn btn-success btn-sm" @click="uploadImage">
          {{ $t('file.cropAndSave') }}
        </button>
      </div>
    </modal>
  </div>
</template>
<script>
import { Cropper, Preview } from 'vue-advanced-cropper'
import apiService from '../../services/api.service'
import MediaService from '../../services/media.service'

export default {
  components: {
    Cropper,
    Preview
  },
  props: {
    aspectRatio: {
      type: Number,
      default: null
    },
    maxHeight: {
      type: Number,
      default: 500
    },
    maxWidth: {
      type: Number,
      default: 500
    },
    entityType: {
      type: String,
      default: ''
    },
    entityUuid: {
      type: String,
      default: ''
    },
    collection: {
      type: String,
      default: ''
    },
    conversion: {
      type: String,
      default: null
    },
    circle: {
      type: Boolean,
      default: false
    },
    minWidth: {
      type: Number,
      default: 0
    },
    minHeight: {
      type: Number,
      default: 0
    }
  },
  data () {
    return {
      loading: false,
      imageUrl: '',
      image: {
        src: null
      },
      cropResult: {
        coordinates: null,
        image: null
      }
    }
  },
  mounted () {
    this.fetchImage()
  },
  methods: {
    zoomIn () {
      this.$refs.cropper.zoom(1.1)
    },
    zoomOut () {
      this.$refs.cropper.zoom(0.9)
    },
    centerCrop () {
      this.$refs.cropper.setCoordinates(({ coordinates, imageSize }) => ({
        left: imageSize.width / 2 - coordinates.width / 2,
        top: imageSize.height / 2 - coordinates.height / 2
      }))
    },
    resetCropper () {
      this.$refs.cropper.reset()
    },
    rotateLeft () {
      this.$refs.cropper.rotate(-90)
    },
    rotateRight () {
      this.$refs.cropper.rotate(90)
    },
    downloadImage () {
      const { canvas } = this.$refs.cropper.getResult()
      if (!canvas) return
      const link = document.createElement('a')
      link.download = 'image.png'
      link.href = canvas.toDataURL()
      link.click()
    },
    onCropChange ({ coordinates, image }) {
      this.cropResult = { coordinates, image }
    },
    cancelCrop () {
      if (this.image.src) {
        URL.revokeObjectURL(this.image.src)
      }
      this.$modal.hide(`crop-modal-${this._uid}`)
      this.image = { src: null }
      this.cropResult = { coordinates: null, image: null }
    },
    uploadImage () {
      const { canvas } = this.$refs.cropper.getResult()
      if (canvas) {
        this.loading = true
        this.$modal.hide(`crop-modal-${this._uid}`)
        this.image = { src: null }
        this.cropResult = { coordinates: null, image: null }

        const form = new FormData()
        // Always upload as PNG so any padding left by the crop (e.g. image
        // smaller than the required dimensions) stays transparent instead
        // of being flattened onto a solid background.
        canvas.toBlob(blob => {
          form.append('file', blob, 'blob.png')
          form.append('type', this.collection)
          form.append('uuid', this.entityUuid)
          form.append('assetType', this.entityType)
          apiService.post(`${process.env.VUE_APP_BASE_URL}api/v1/admin/media/upload`, form).then(() => {
            this.$toast.fire({
              title: this.$t('image.uploaded'),
              icon: 'success'
            })
            this.fetchImage()
          }).catch(() => {
            this.loading = false
          })
        }, 'image/png')
      }
    },
    loadImage (event) {
      const { files } = event.target
      if (!files || !files[0]) return

      if (this.image.src) URL.revokeObjectURL(this.image.src)
      const blob = URL.createObjectURL(files[0])

      const proceed = () => {
        this.image = { src: blob }
        this.cropResult = { coordinates: null, image: null }
        this.$modal.show(`crop-modal-${this._uid}`)
      }

      if (this.minWidth || this.minHeight) {
        const img = new Image()
        img.onload = () => {
          if ((this.minWidth && img.naturalWidth < this.minWidth) ||
              (this.minHeight && img.naturalHeight < this.minHeight)) {
            URL.revokeObjectURL(blob)
            this.$toast.fire({
              title: this.$t('image.tooSmall', { minWidth: this.minWidth, minHeight: this.minHeight }),
              icon: 'error'
            })
            return
          }
          proceed()
        }
        img.src = blob
      } else {
        proceed()
      }
    },
    fetchImage () {
      this.loading = true
      MediaService.getFirstImageByType(this.entityType, this.entityUuid, this.collection, this.conversion).then((res) => {
        this.imageUrl = res.data
      }).finally(() => {
        this.loading = false
      })
    },
    deleteImage () {
      MediaService.deleteByType(this.entityType, this.entityUuid, this.collection).then(() => {
        this.$toast.fire({
          title: this.$t('image.deleted'),
          icon: 'success'
        })
        this.fetchImage()
      })
    }
  },
  destroyed () {
    if (this.image.src) {
      URL.revokeObjectURL(this.image.src)
    }
  }
}
</script>
<style lang="scss">
.ntm-cropper {
  max-height: 500px;
}

.crop-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-top: 1px solid #e4e7ed;
  gap: 8px;
}

.crop-controls {
  display: flex;
  align-items: center;
  gap: 6px;
}

.crop-preview-wrap {
  border: 1px solid #e4e7ed;
  overflow: hidden;
  border-radius: 4px;
  flex-shrink: 0;
  width: 70px;
  height: 70px;
}

.crop-preview-circle {
  border-radius: 50%;
}

.file-drop-area {
  position: relative;
  display: flex;
  color: white;
  justify-content: center;
  align-items: center;
  border-radius: 3px;
  transition: 0.2s;

  &.is-active {
    background-color: rgba(255, 255, 255, 0.05);
  }
}

.file-wrapper {
  border: 2px solid transparent;
  transition: 0.2s ease-in;

  &:hover,
  &:focus,
  &:active {
    border: 2px solid #5c80d1;
  }
}

.file-overlay {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  background: rgba(0,0,0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
}

.crop-actions {
  gap: 16px;
}

.crop-image {
  max-width: 400px;
  width: 100%;
}

.upload-cog {
  font-size: 26px;
}

.fake-btn {
  flex-shrink: 0;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 3px;
  font-weight: 700;
  padding: 8px 15px;
  margin-right: 10px;
  font-size: 12px;
  text-transform: uppercase;
}

.file-msg {
  font-size: 14px;
  line-height: 1.4;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-input {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  cursor: pointer;
  opacity: 0;

  &:focus {
    outline: none;
  }
}
</style>
